import React, { useEffect, useState } from 'react';
import { ClipCostSummary } from '../../api/clip';
import { ClipPrompt } from '../../api/structs/clip';
import { getFileType } from '../../api/structs/clip';
import { MediaProfile } from '../../api/structs/media-spec';
import { MediaItem } from '../../api/structs/media';
import ClipRating from '../idea-library/ClipRating';
import { Account, User } from '../../api/structs/user';
import { Card, Badge, ExpandableSection, Button } from '../ui';
import OutputGallery from './OutputGallery';
import { MediaEditorSection, ScheduleSection, MusicTrack, AdvancedSettings } from './sections';
import { EditClipPromptModal, ConfirmModal } from '../modals';
import { useCostDisplay } from '../../hooks/useCostDisplay';
import ClipPromptItemHeader from './ClipPromptItemHeader';
import ClipCollapsedThumbnails from './ClipCollapsedThumbnails';
import ClipRenderActions from './ClipRenderActions';
import ClipTelegramActions from './ClipTelegramActions';
import ClipQualityExportAction from './ClipQualityExportAction';
import { extractCaption } from './clipPromptItemHelpers';
import { useClipDraftEditor } from './useClipDraftEditor';
import { useClipActions } from './useClipActions';
import { isCancelledRender, seedClipRenderState, useClipRenderState } from './renderState';
import { useClipStyleKind, useClipStyleRenderable } from './clipStyleRegistry';
import { carouselSlidePaths, isCarouselStyle } from './carouselSlides';
import { clipStyleLabel } from './clipStyleDisplay';
import { isEditorClipStyle } from '../clip-editor/clipEditorStyles';


const EMPTY_MEDIA: MediaItem[] = [];
interface ClipPromptItemProps {
  clip: ClipPrompt;
  costSummary: ClipCostSummary | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRefresh: () => void;
  mediaProfile: MediaProfile;
  activeAccount: Account | null;
  activeUser: User | null;
  /** Opens the full-stage video editor; absent outside the studio shell. */
  onOpenEditor?: (clipId: string) => void;
}

const ClipPromptItem: React.FC<ClipPromptItemProps> = ({
  clip,
  costSummary,
  isExpanded,
  onToggleExpand,
  onRefresh,
  mediaProfile,
  activeAccount,
  activeUser,
  onOpenEditor,
}) => {
  const display = useCostDisplay();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // A missing bucket must fall back to a STABLE empty array. `|| []` minted a
  // fresh array per render, which read as "the media changed" to everything
  // keyed on these identities downstream (scene-group rebuilds, run-frame
  // re-reads) — on a video-only reel, whose image bucket is always absent,
  // that amplified every re-render into scene rebuilds and media refetches.
  const images = clip.media?.images || EMPTY_MEDIA;
  const aiVideos = clip.media?.ai_videos || EMPTY_MEDIA;
  const audios = clip.media?.audios || EMPTY_MEDIA;
  const totalMedia = images.length + aiVideos.length + audios.length;

  // Keep the shared render-state store reconciled with the served clip fields so
  // a page reload mid-render (or a persisted failure) still shows the bar/strip.
  useEffect(() => {
    seedClipRenderState(clip.id, {
      render_status: clip.render_status,
      render_progress: clip.render_progress,
      render_error: clip.render_error,
    });
  }, [clip.id, clip.render_status, clip.render_progress, clip.render_error]);
  const renderState = useClipRenderState(clip.id);

  const {
    draftClipMetadata,
    draftImages,
    draftAiVideos,
    draftAudios,
    pendingSaves,
    isRenderStale,
    openEditor,
    handlePendingSaveChange,
    waitForPendingSaves,
    markDraftDirty,
  } = useClipDraftEditor(clip, images, aiVideos, audios, onRefresh);

  const fileUrls = clip.file_urls || [];
  const clipCaption = extractCaption(clip);
  const hasOutput =
    fileUrls.length > 0 &&
    fileUrls.some((url) => getFileType(url) !== 'unknown') &&
    !fileUrls.some((url) => url.includes('waiting'));
  const clipStyle = clip.style?.style || 'standard';
  const isCarouselClip = clipStyle === 'genericCarousel';
  const styleRenderable = useClipStyleRenderable(clipStyle);
  // Any carousel (technologyCarousel too) posts its whole slide set, video slides included.
  const postsAsCarousel = isCarouselStyle(clipStyle, useClipStyleKind(clipStyle));
  const carouselSlides = postsAsCarousel ? carouselSlidePaths(fileUrls) : [];

  const {
    isRendering,
    isSendingTelegram,
    telegramSendLabelText,
    isSendingCarousel,
    isCancellingRender,
    handleDelete,
    handleRender,
    handleCancelRender,
    handleSendToTelegram,
    handleSendCarouselToTelegram,
  } = useClipActions(clip.id, fileUrls, clipCaption, activeUser, onRefresh, waitForPendingSaves, carouselSlides);

  const hasVideoOutput = hasOutput && fileUrls.some((url) => /\.(mp4|webm|mov)$/i.test(url));
  // Prefer the shared render state; keep local isRendering as the optimistic
  // bridge until the first WS event lands.
  const isActivelyRendering = renderState.status === 'rendering' || isRendering;
  const renderButtonLabel = isCancelledRender(renderState)
    ? 'Render again'
    : renderState.status === 'failed'
      ? 'Retry render'
      : isActivelyRendering
        ? 'Rendering...'
        : pendingSaves > 0
          ? 'Saving...'
          : hasOutput && isRenderStale
            ? 'Render updates'
            : hasOutput
              ? 'Re-render'
              : 'Render';
  const renderDisabledReason =
    styleRenderable === false
      ? `${clipStyleLabel(clipStyle)} clips can't be rendered here.`
      : undefined;

  // The video editor drives the reel/quote compositions; carousel clips keep
  // their existing storyboard surface.
  const canOpenEditor =
    Boolean(onOpenEditor) && isEditorClipStyle(clipStyle) && styleRenderable !== false;

  const canPublish =
    hasOutput &&
    (Boolean(activeAccount) ||
      (hasVideoOutput && Boolean(activeUser)) ||
      (postsAsCarousel && carouselSlides.length > 0 && Boolean(activeUser)));

  return (
    <>
      <Card hover className="animate-fade-in">
        <Card.Body className="overflow-hidden">
          <ClipPromptItemHeader
            clipName={clip.name}
            clipStyle={clipStyle}
            styleRenderable={styleRenderable}
            costSummary={costSummary}
            display={display}
            isCarouselClip={isCarouselClip}
            onOpenAiChat={(e) => {
              e.stopPropagation();
              openEditor(clip.id);
            }}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
            onOpenSettings={() => setShowEditModal(true)}
            onOpenDeleteConfirm={() => setShowDeleteConfirm(true)}
          />

          {/* Collapsed: Thumbnails */}
          {!isExpanded && images.length > 0 && <ClipCollapsedThumbnails images={images} />}

          {/* Expanded: purpose-grouped sections */}
          {isExpanded && (
            <div className="space-y-3 mt-4">
              {/* Storyboard — media & prompts */}
              <ExpandableSection
                title="Storyboard"
                badge={<Badge variant="gray">{totalMedia}</Badge>}
                defaultExpanded={true}
              >
                <MediaEditorSection
                  clipId={clip.id}
                  images={draftImages}
                  aiVideos={draftAiVideos}
                  audios={draftAudios}
                  clipStyle={clipStyle}
                  clipMetadata={draftClipMetadata}
                  onRefresh={onRefresh}
                  onPendingSaveChange={handlePendingSaveChange}
                  onDraftDirty={markDraftDirty}
                  mediaProfile={mediaProfile}
                />
              </ExpandableSection>

              {/* Style & Text — the renderer-driving fields (edited in Settings) */}
              <ExpandableSection title="Style & Text" defaultExpanded={false}>
                <div className="space-y-3 py-1">
                  <p className="text-xs text-zinc-500 font-mono">
                    Style, headline and on-screen text fields drive the renderer.
                  </p>
                  <Button variant="secondary" size="sm" onClick={() => setShowEditModal(true)}>
                    Edit style &amp; text fields
                  </Button>
                </div>
              </ExpandableSection>

              {/* Audio — music, narration & subtitles */}
              {!isCarouselClip && (
                <ExpandableSection title="Audio" defaultExpanded={false}>
                  <div className="space-y-3">
                    <MusicTrack clipId={clip.id} clip={clip} onRefresh={onRefresh} />
                    <AdvancedSettings clipId={clip.id} clip={clip} onRefresh={onRefresh} />
                  </div>
                </ExpandableSection>
              )}

              {canOpenEditor && (
                <Button variant="secondary" size="sm" onClick={() => onOpenEditor?.(clip.id)}>
                  Open editor
                </Button>
              )}

              {/* Render CTA + live progress/error */}
              <ClipRenderActions
                renderState={renderState}
                isRendering={isActivelyRendering}
                pendingSaves={pendingSaves}
                renderButtonLabel={renderButtonLabel}
                onRender={handleRender}
                renderDisabledReason={renderDisabledReason}
                onCancel={handleCancelRender}
                isCancelling={isCancellingRender}
              />

              {/* Output */}
              {hasOutput ? (
                <ExpandableSection title="Output" defaultExpanded={true}>
                  <OutputGallery fileUrls={fileUrls} clipStyle={clipStyle} />
                  {/* The verdict lives where the finished video is watched. */}
                  <ClipRating clipId={clip.id} />
                </ExpandableSection>
              ) : (
                <ExpandableSection
                  title="Output"
                  badge={<Badge variant="yellow">Not rendered</Badge>}
                  defaultExpanded={false}
                  disabled={true}
                >
                  <div />
                </ExpandableSection>
              )}

              {/* Publish — schedule & Telegram distribution */}
              {canPublish && (
                <ExpandableSection
                  title="Publish"
                  badge={activeAccount ? <Badge variant="blue">{activeAccount.username}</Badge> : undefined}
                  defaultExpanded={false}
                >
                  <div className="space-y-3">
                    {activeAccount && (
                      <ScheduleSection
                        clipId={clip.id}
                        initialCaption={clipCaption}
                        activeAccount={activeAccount}
                        fileUrls={fileUrls}
                      />
                    )}
                    <ClipTelegramActions
                      hasVideoOutput={hasVideoOutput}
                      activeUser={activeUser}
                      isSendingTelegram={isSendingTelegram}
                      telegramSendLabelText={telegramSendLabelText}
                      onSendToTelegram={handleSendToTelegram}
                      isCarouselClip={postsAsCarousel}
                      orderedImagePathsCount={carouselSlides.length}
                      isSendingCarousel={isSendingCarousel}
                      onSendCarouselToTelegram={handleSendCarouselToTelegram}
                    />
                  </div>
                </ExpandableSection>
              )}

              {/* Evidence export — secondary, so it stays a quiet link. */}
              <ClipQualityExportAction clipId={clip.id} metadata={clip.metadata} />
            </div>
          )}
        </Card.Body>
      </Card>

      <EditClipPromptModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        clip={clip}
        onSave={onRefresh}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Clip"
        message={`Are you sure you want to delete "${clip.name || 'this clip'}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default ClipPromptItem;
